//@ts-strict-ignore
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import ProductUsageQuery from '../../graphql/queries/product/ProductUsageQuery.graphql';
import Dropdown from '../../components/connectedForm/Dropdown';
import * as input from '../../components/connectedForm/inputComponents';
import { default as Form } from '../../components/connectedForm';
import GridContainer from '../../components/connectedForm/GridContainer';
import EditableDataGrid from '../../components/EditableDataGrid';
import Formatters from '../../components/grid-addons/formatters';
import ColumnLabel from '../../components/connectedForm/ColumnLabel';
import Label from '../../components/connectedForm/Label';
import {
  IProductAvailabilityValue,
  IProductsWarehousesQueryResult,
  IProductsWarehousesQueryVariables,
  IProductUsage,
  IProductUsageQueryResult,
  IProductUsageQueryVariables,
  IWarehouse,
  Maybe,
} from '../../graphql/schema/p4types';
import ValueField from '../../components/connectedForm/ValueField';

import UserContext from '../../components/Context/UserContext';
import WarehousesQuery from '../../graphql/queries/product/ProductsWarehousesQuery.graphql';
import { Tooltip } from '@fluentui/react-components';
import Row from '../../components/react-data-grid/Row';
import BlurTextField from '../../components/connectedForm/BlurTextField';

/**
 * Custom hook to handle state for a selected warehouse.
 *  - Defaults to company user 'sales' warehouse.
 *  - If not available, and company has only a single warehouse - Defaults to that warehouse instead.
 */
const useWarehouseState: () => [Maybe<IWarehouse>, Dispatch<SetStateAction<Maybe<IWarehouse>>>] = () => {
  const userContext = useContext(UserContext);
  const defaultWarehouse = userContext.currentUser.companyUser.salesWarehouse;

  const [warehouse, setWarehouse] = useState<Maybe<IWarehouse>>(defaultWarehouse);

  //If no default warehouse (Per company user) was found, query warehouses so we can default from there (if there is only one warehouse).
  const { data: allWarehousesData } = useQuery<IProductsWarehousesQueryResult, IProductsWarehousesQueryVariables>(
    WarehousesQuery,
    { skip: !!defaultWarehouse }
  );

  useEffect(() => {
    if (!warehouse && allWarehousesData?.listData?.edges?.length === 1) {
      setWarehouse(allWarehousesData.listData.edges[0]!.node as Maybe<IWarehouse>);
    }
  }, [allWarehousesData]);

  return [warehouse, setWarehouse];
};

const AvailabilityRowRenderer = (props) => {
  const row = props.row;
  if (row?.outlier) {
    return (
      <Tooltip
        content={'This period is an outlier and has not been included in the calculation'}
        relationship={'label'}
        appearance={'inverted'}
        withArrow
      >
        <div>
          <Row {...props} cellClassName="outlier" />
        </div>
      </Tooltip>
    );
  }
  return <Row {...props} />;
};

const ProductAvailability = ({ product }) => {
  const [warehouses, setWarehouses] = useState<Array<IWarehouse>>([]);
  const [warehouse, setWarehouse] = useWarehouseState();

  const [productUsage, setProductUsage] = useState<IProductUsage | null>(null);

  useEffect(() => {
    if ((!warehouses || warehouses.length === 0) && product.productWarehouseDetails) {
      const wh: Array<IWarehouse> = [];

      for (const productWarehouseDetail of product.productWarehouseDetails) {
        if (productWarehouseDetail && productWarehouseDetail.warehouse) {
          wh.push(productWarehouseDetail.warehouse);
        }
      }

      setWarehouses(wh);
    }
  }, [product]);

  const AVAILABILITY_COLUMN_META = [
    {
      name: 'Year',
      key: 'year',
      editable: false,
    },
    {
      name: 'Month',
      key: 'month',
      editable: false,
    },
    {
      name: 'Usage',
      key: 'usage',
      editable: false,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: () => product?.precision || 0 },
      },
    },
  ];

  const [productUsageQuery] = useLazyQuery<IProductUsageQueryResult, IProductUsageQueryVariables>(ProductUsageQuery, {
    fetchPolicy: 'network-only',
    onCompleted: ({ data }: { data: IProductUsage }) => {
      setProductUsage(data);
    },
    onError: (data) => {
      setProductUsage(null);
    },
  });

  useEffect(() => {
    if (product.id && warehouse?.id) {
      productUsageQuery({
        variables: {
          productId: product.id,
          warehouseId: warehouse.id,
        },
      });
    }
  }, [product.id, warehouse?.id]);

  const precision = product?.precision || 0;

  return (
    <>
      <Form.Legend label="Warehouse" />
      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <Dropdown
            options={warehouses}
            component={input.Dropdown}
            label="Warehouse"
            onChange={(selected: IWarehouse) => {
              setWarehouse(selected);
            }}
            value={warehouse ? warehouse.id : null}
            valueKey="id"
            labelKey="description"
          />
        </Form.Fieldset>
      </Form.Row>
      {productUsage ? (
        <React.Fragment>
          <Form.Legend label="Totals & Averages" />
          <Form.Row>
            <Form.Fieldset cols={3 / 4}>
              <Form.Row>
                <Form.Fieldset cols={1 / 3}>
                  <ValueField
                    label="Months in Average Calculation"
                    value={productUsage.monthsInAvailabilityCalculation?.toString()}
                    labelCols={3 / 4}
                    inputCols={1 / 4}
                    readOnly={true}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  <BlurTextField
                    label="Discard Outliers"
                    value={productUsage.discardOutliers ? 'Yes' : 'No'}
                    labelCols={3 / 4}
                    inputCols={1 / 4}
                    readOnly={true}
                    hint={'Standard deviation is used to determine which periods are outliers'}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={1 / 3}>
                  <ValueField
                    label="Total Usage"
                    value={productUsage.totalUsage}
                    decimals={precision}
                    labelCols={3 / 4}
                    inputCols={1 / 4}
                    readOnly={true}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  <ValueField
                    label="Total Usage for Average Calculation"
                    value={productUsage.totalForAverageCalculation}
                    decimals={precision}
                    labelCols={3 / 4}
                    inputCols={1 / 4}
                    readOnly={true}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  <ValueField
                    label="Average per Month"
                    value={productUsage.averagePerPeriod}
                    decimals={precision && precision > 0 ? precision : 2}
                    labelCols={3 / 4}
                    inputCols={1 / 4}
                    readOnly={true}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <Form.Legend label="Usage History" />
              <GridContainer alwaysActive={true}>
                <EditableDataGrid
                  dataid="productUsage"
                  testId="productUsage"
                  listData={productUsage.productUsageLines ? productUsage.productUsageLines : []}
                  rowsCount={productUsage.productUsageLines?.length}
                  columns={AVAILABILITY_COLUMN_META}
                  rowRenderer={AvailabilityRowRenderer}
                  enableCellSelect={false}
                  addBlankRow={false}
                  autoContentHeight={true}
                />
              </GridContainer>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <Form.Legend label="Current Levels" />
              <Form.Row>
                <Form.Fieldset cols={1 / 3}>
                  <ColumnLabel label="" />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  <ValueField value="Quantity" view={true} align="right" />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  <ValueField value="Months" view={true} align="right" />
                </Form.Fieldset>
              </Form.Row>
              {ProductAvailability.renderLevelRow(
                'Minimum Level',
                productUsage.productAvailability?.minimumLevelAvailability,
                precision
              )}
              {ProductAvailability.renderLevelRow(
                'Maximum Level',
                productUsage.productAvailability?.maximumLevelAvailability,
                precision
              )}
              {ProductAvailability.renderLevelRow(
                'Optimum Level',
                productUsage.productAvailability?.optimumLevelAvailability,
                precision
              )}
              {ProductAvailability.renderLevelRow(
                'Level',
                productUsage.productAvailability?.levelAvailability,
                precision
              )}
              {ProductAvailability.renderLevelRow(
                'Free Stock',
                productUsage.productAvailability?.freeStockAvailability,
                precision
              )}
            </Form.Fieldset>
          </Form.Row>
        </React.Fragment>
      ) : null}
      {!productUsage && warehouse ? <div>No Usage Data available for the selected warehouse</div> : null}
    </>
  );
};

export default ProductAvailability;

ProductAvailability.renderLevelRow = (
  label: string,
  availabilityValue: Maybe<IProductAvailabilityValue>,
  precision: number = 0
) => {
  return (
    <Form.Row>
      <Form.Fieldset cols={1 / 3}>
        <Label>{label}</Label>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <ValueField
          view={true}
          primary={false}
          decimals={precision}
          value={availabilityValue ? availabilityValue.quantity : 'N/A'}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <ValueField
          view={true}
          primary={false}
          decimals={precision && precision > 0 ? precision : 2}
          value={availabilityValue ? availabilityValue.period : 'N/A'}
        />
      </Form.Fieldset>
    </Form.Row>
  );
};
